import type { SearchResponse } from '@algolia/client-search'
import { i18n } from '@lingui/core'
import algoliasearch from 'algoliasearch'
import classNames from 'clsx'
import { arrayOf, bool, node, shape, string } from 'prop-types'
import { Component } from 'react'

import { IS_JEST, IS_STORYBOOK, TENANT_IT } from 'shared/consts'
import { NEW_CDN } from 'shared/experiments/consts'
import { getExperiment } from 'shared/experiments/utils/experiments'
import store from 'shared/store'
import { type InferPropsWithDefaults } from 'shared/utils/propTypes'
import {
  convertHitToHistorySuggestion,
  convertHitToSuggestion,
  getTopProductsAndSearches,
} from 'views/components/organisms/PreQuerySuggestions/helpers'
import LoadingWrapper from 'views/components/organisms/PreQuerySuggestions/LoadingWrapper'
import TopProducts from 'views/components/organisms/PreQuerySuggestions/TopProducts'
import TopSearches from 'views/components/organisms/PreQuerySuggestions/TopSearches'
import type {
  SearchHistorySuggestion,
  SearchHit,
} from 'views/components/organisms/PreQuerySuggestions/types'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import { publicRuntimeConfigType } from 'views/utils/propTypes'

import PopularCategoriesOrBrands from './PopularCategoriesOrBrands'

import './PreQuerySuggestions.scss'

class PreQuerySuggestions extends Component<ComponentProps, ComponentState> {
  state: ComponentState = this.getInitialState()

  constructor(props: ComponentProps) {
    super(props)

    if (IS_JEST || IS_STORYBOOK) {
      // Don't disturb Chromatic
      return
    }
  }

  getInitialState(): ComponentState {
    const { topProducts, topSearches } = this.props

    // Top searches & products can be passed through the props:
    // it is used only for the Storybook stories
    return {
      topSearches: Array.isArray(topSearches) ? topSearches : [],
      topProducts: Array.isArray(topProducts) ? topProducts : [],
    } as unknown as ComponentState // TS linter can't comprehend it is safe
  }

  componentDidMount() {
    if (IS_JEST || IS_STORYBOOK) {
      return
    }

    const {
      publicRuntimeConfig: {
        publicConfig: { cdnUrl },
        pageProperties: { expa },
      },
      mobile,
    } = this.props

    const {
      pageProperties: { experiments },
      publicConfig: {
        tenant,
        searchConfig: { algolia },
      },
    } = store.getPublicRuntimeConfig()
    const cdnEnabled = getExperiment(NEW_CDN, experiments)
    const processResults = (results: SearchResponse[]) => {
      const [topSearchesResults, topProductsResutls] = results
      const topProducts = topProductsResutls?.hits.map(product =>
        convertHitToSuggestion({
          cdnUrl,
          product: product as SearchHit,
          queryID: topProductsResutls?.queryID,
          isRebrandingEnabled: tenant === TENANT_IT,
          placeholderFromCdn: cdnEnabled.isEnabled,
        })
      )

      const topSearches = topSearchesResults?.hits.map(product =>
        convertHitToHistorySuggestion({
          product: product as SearchHit,
          queryID: topSearchesResults.queryID,
        })
      )
      this.setState({ topSearches })
      this.setState({ topProducts })
    }

    const deviceClass = mobile ? 'mobile' : 'desktop'

    const preQuerySuggestionsSession = sessionStorage.getItem(
      'preQuerySuggestions'
    )
    if (preQuerySuggestionsSession) {
      const preQuerySuggestions = JSON.parse(preQuerySuggestionsSession)
      if (Object.keys(preQuerySuggestions).length > 0) {
        processResults(preQuerySuggestions.results)
      }
    } else {
      const searchClient = algoliasearch(algolia.appId, algolia.apiKey, {
        headers: {
          'User-Agent': 'fock - PreQuerySuggestions',
        },
      })
      void getTopProductsAndSearches({
        deviceClass,
        expa,
        searchClient,
        algolia,
      }).then(response => {
        sessionStorage.setItem('preQuerySuggestions', JSON.stringify(response))
        processResults(response.results)
      })
    }
  }

  render() {
    const {
      hasSearchHistory,
      mobile,
      children,
      query,
      publicRuntimeConfig: {
        publicConfig: { suggestBrands, suggestCategories },
      },
    } = this.props

    const { topSearches, topProducts } = this.state
    const hasTopProducts = Boolean(topProducts?.length)
    const hasTopSearches = Boolean(topSearches?.length)
    const deviceClass = mobile ? 'mobile' : 'desktop'
    const topBrands = suggestBrands?.[deviceClass] ?? []
    const topCategories = suggestCategories?.[deviceClass] ?? []
    let title = i18n._('general.label.popularCategories')

    const getTopBrandsOrCategories = () => {
      if (topBrands.length > 0) {
        title = i18n._('general.label.popularBrands')
        return topBrands
      } else if (topCategories.length > 0) {
        return topCategories
      } else {
        return []
      }
    }
    const topBrandsOrCategories = getTopBrandsOrCategories()

    if (!topSearches?.length || query) {
      return <LoadingWrapper mobile={mobile} />
    }

    return (
      <div
        className={classNames('o-PreQuerySuggestions__results', {
          'o-SearchBox__results--mobile': mobile,
          'o-SearchBox__results u-padding-x-small': !mobile,
        })}
      >
        <div className="l-flex l-flex--start">
          {hasSearchHistory && (
            <div className="u-padding--right@medium u-padding-small--right">
              {children}
            </div>
          )}
          {hasTopSearches && (
            <TopSearches
              hits={topSearches}
              hasSearchHistory={hasSearchHistory}
            />
          )}
          {topBrandsOrCategories?.length > 0 && (
            <PopularCategoriesOrBrands
              listItems={
                topBrandsOrCategories as { name: string; url: string }[]
              }
              title={title}
            />
          )}
        </div>
        {hasTopProducts && (
          <TopProducts hits={topProducts} mobile={Boolean(mobile)} />
        )}
      </div>
    )
  }
}

const propTypes = {
  publicRuntimeConfig: shape(publicRuntimeConfigType).isRequired,
  hasSearchHistory: bool,
  children: node,
  mobile: bool,
  topSearches: arrayOf(shape({})),
  topProducts: arrayOf(shape({})),
  query: string,
}

const defaultProps = {
  hasSearchHistory: false,
  children: null,
  mobile: false,
  topSearches: null,
  topProducts: null,
  query: '',
}

type ComponentProps = InferPropsWithDefaults<
  typeof propTypes,
  typeof defaultProps
>

type ComponentState = {
  topSearches: SearchHistorySuggestion[]
  topProducts: SearchHit[]
}

export default withShopAndRuntimeConfig(PreQuerySuggestions)
